import type {
  CanonicalTreeEdgeKind,
} from './CanonicalTreeRelationshipEdge';

export interface TreeConnectorAuthorityRecord {
  readonly connector: string;
  readonly authority: string;
  readonly governed: boolean;
  readonly canonicalEdgeKind: CanonicalTreeEdgeKind | null;
  readonly legacyAlias: boolean;
}

const canonical = (
  connector: CanonicalTreeEdgeKind,
): TreeConnectorAuthorityRecord =>
  Object.freeze({
    connector,
    authority: 'Tree Governor',
    governed: true,
    canonicalEdgeKind: connector,
    legacyAlias: false,
  });

const legacy = (
  connector: string,
  canonicalEdgeKind: CanonicalTreeEdgeKind,
): TreeConnectorAuthorityRecord =>
  Object.freeze({
    connector,
    authority: 'Tree Governor',
    governed: true,
    canonicalEdgeKind,
    legacyAlias: true,
  });

/**
 * CC-TREE-LIVE-003G3WJ2
 *
 * Connector authority must cover every canonical edge class. Legacy aliases
 * remain exported for old consumers, but they never define a second semantic
 * model.
 */
const TREE_CONNECTOR_AUTHORITIES: readonly TreeConnectorAuthorityRecord[] =
Object.freeze([
  canonical('parent-child'),
  canonical('sibling'),
  canonical('spouse-partner'),
  canonical('co-parent'),
  canonical('step-family'),
  canonical('in-law'),
  canonical('guardian'),
  canonical('foster-family'),
  canonical('chosen-family'),
  canonical('god-family'),
  canonical('affiliated-family'),

  // Compatibility aliases retained for historical callers/verifiers.
  legacy('spouse', 'spouse-partner'),
  legacy('grandparent', 'parent-child'),
]);

export const REQUIRED_CANONICAL_TREE_CONNECTORS:
readonly CanonicalTreeEdgeKind[] = Object.freeze([
  'parent-child',
  'sibling',
  'spouse-partner',
  'co-parent',
  'step-family',
  'in-law',
  'guardian',
  'foster-family',
  'chosen-family',
  'god-family',
  'affiliated-family',
]);

export function getTreeConnectorAuthorities():
readonly TreeConnectorAuthorityRecord[] {
  return TREE_CONNECTOR_AUTHORITIES;
}

export function hasCanonicalTreeConnectorAuthority(
  edgeKind: CanonicalTreeEdgeKind,
): boolean {
  return TREE_CONNECTOR_AUTHORITIES.some(
    record =>
      !record.legacyAlias &&
      record.canonicalEdgeKind === edgeKind &&
      record.governed,
  );
}
